import { useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import { apiRetryErrorAtom } from '../store/globalStore/stateSegments/apiRetryErrorSegment';
import { setOnMaxRetriesExhausted } from '../utils/httpClient';

/**
 * Hook to manage the API retry error modal state
 * Sets up the callback from httpClient to show the modal when max retries are exhausted
 */
export const useApiRetryErrorModal = () => {
  const [modalState, setModalState] = useAtom(apiRetryErrorAtom);

  // Open the modal
  const openModal = useCallback(() => {
    setModalState({
      isOpen: true,
      title: 'Something went wrong.',
      description: 'Please retry your content creation.',
    });
  }, [setModalState]);

  // Close the modal
  const closeModal = useCallback(() => {
    setModalState(prev => ({
      ...prev,
      isOpen: false,
    }));
  }, [setModalState]);

  // Handle continue button - refresh the page
  const handleContinue = useCallback(() => {
    closeModal();
    window.location.reload();
  }, [closeModal]);

  // Handle cancel button - just close the modal
  const handleCancel = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // Set up the callback from httpClient
  useEffect(() => {
    setOnMaxRetriesExhausted(openModal);

    // Cleanup on unmount
    return () => {
      setOnMaxRetriesExhausted(() => {});
    };
  }, [openModal]);

  return {
    isOpen: modalState.isOpen,
    title: modalState.title,
    description: modalState.description,
    handleContinue,
    handleCancel,
    closeModal,
  };
};
